import { useCallback, useState } from "react";
import { Calculator } from "lucide-react";
import { errorMessage } from "../../../api/client";
import { money, payApi, type PreviewResult } from "../../../api/pay";
import { adminApi } from "../../../api/resources";
import { Button, ErrorText, TextInput } from "../../../components/ui";
import { DataTable, useLoad } from "../shared";

const sel = "h-11 rounded-sm border border-white/15 bg-surface px-3 text-sm text-white";
const day = (offset: number) => new Date(Date.now() + offset * 86400000).toISOString().slice(0, 10);
const la = (iso: string) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Los_Angeles", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit", timeZoneName: "short" });

function Issues({ title, items, testId, tone }: { title: string; items: PreviewResult["blocking"]; testId: string; tone: string }) {
  if (!items.length) return null;
  return (
    <div className={`rounded-sm border p-4 ${tone}`} data-testid={testId}>
      <p className="label-caps mb-2">{title}</p>
      {items.map((b, i) => <p key={i} className="text-xs"><span className="font-mono font-semibold">{b.code}</span> {b.session_id && `· ${b.session_id.slice(0, 8)}`} — {b.message}</p>)}
    </div>
  );
}

export function PreviewTab() {
  const loader = useCallback(() => adminApi.employees(), []);
  const { data: employees } = useLoad(loader);
  const [emp, setEmp] = useState("");
  const [from, setFrom] = useState(day(-13));
  const [to, setTo] = useState(day(0));
  const [res, setRes] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(false);
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      setRes(await payApi.preview(emp, from, to, draft));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section data-testid="pay-preview-tab" className="space-y-6">
      <div className="flex flex-wrap items-end gap-2">
        <select className={sel} value={emp} onChange={(e) => setEmp(e.target.value)} data-testid="preview-employee-select"><option value="">Employee…</option>{employees?.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}</select>
        <div className="w-44"><TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="preview-from-input" /></div>
        <div className="w-44"><TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="preview-to-input" /></div>
        <label className="flex h-11 items-center gap-2 text-xs text-zinc-300"><input type="checkbox" checked={draft} onChange={(e) => setDraft(e.target.checked)} className="h-4 w-4 accent-[#ff1d00]" data-testid="preview-include-unapproved" />Include unapproved payable time (draft)</label>
        <Button onClick={run} loading={busy} disabled={!emp} data-testid="preview-run-btn"><Calculator className="h-4 w-4" aria-hidden />Calculate</Button>
      </div>
      <ErrorText message={error} testId="preview-error" />
      {res && (
        <div className="space-y-6" data-testid="preview-result">
          {res.draft && <p className="inline-block rounded-sm border border-amber-500/50 bg-amber-500/10 px-3 py-1 font-display text-sm font-bold uppercase tracking-widest text-amber-200" data-testid="preview-draft-label">DRAFT · policy v{res.policy_version} {res.banner.status}</p>}
          <p className="text-xs text-zinc-500">{res.sessions_considered} session(s) considered · {res.mw_threshold_note}</p>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {res.periods.map((p) => (
              <div key={p.id} className="rounded-sm border border-white/10 bg-surface p-5" data-testid={`preview-period-${p.id}`}>
                <p className="label-caps text-zinc-400">{p.label}</p>
                <p className="mt-2 font-display text-3xl font-bold text-white" data-testid={`preview-gross-${p.id}`}>{money(p.gross_wages_cents)}</p>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500">{res.label}</p>
                <dl className="mt-3 grid grid-cols-2 gap-1 text-xs text-zinc-300">
                  <dt>Regular</dt><dd className="text-right font-mono">{money(p.regular_cents)}</dd>
                  <dt>Daily OT</dt><dd className="text-right font-mono">{money(p.daily_ot_cents)}</dd>
                  <dt>Weekly OT</dt><dd className="text-right font-mono">{money(p.weekly_ot_cents)}</dd>
                  <dt>Weekly OT true-up</dt><dd className="text-right font-mono">{money(p.weekly_ot_true_up_cents)}</dd>
                  <dt>Adjustments</dt><dd className="text-right font-mono">{money(p.adjustments_cents)}</dd>
                </dl>
                {p.weekly_ot_note && <p className="mt-2 text-xs text-amber-300" data-testid={`preview-weekly-pending-${p.id}`}>{p.weekly_ot_note}</p>}
              </div>
            ))}
          </div>
          <Issues title="Blocking exceptions (excluded)" items={res.blocking} testId="preview-blocking" tone="border-red-500/40 bg-red-500/10 text-red-200" />
          <Issues title="Warnings (informational)" items={res.warnings} testId="preview-warnings" tone="border-amber-500/40 bg-amber-500/10 text-amber-200" />
          <DataTable headers={["Segment (LA time)", "Class", "Rate", "Seconds", "Hours", "Exact ¢", "Cents"]} testId="preview-lines" empty={!res.lines.length}>
            {res.lines.map((l, i) => (
              <tr key={i} className="border-t border-white/5 font-mono text-xs" data-testid={`preview-line-${i}`}>
                <td className="px-5 py-2 text-zinc-300">{la(l.start)} → {la(l.end)}</td>
                <td className="px-5 py-2 text-white">{l.classification} ×{l.multiplier}{l.draft_unapproved && <span className="ml-1 rounded-sm bg-amber-400/20 px-1 text-[9px] font-bold text-amber-200" data-testid={`preview-line-draft-${i}`}>DRAFT · unapproved</span>}</td>
                <td className="px-5 py-2">{money(l.rate_cents)}</td>
                <td className="px-5 py-2">{l.seconds}</td>
                <td className="px-5 py-2">{l.hours}</td>
                <td className="px-5 py-2 text-zinc-400">{l.amount_exact}</td>
                <td className="px-5 py-2 text-white">{l.cents ?? "—"}</td>
              </tr>
            ))}
          </DataTable>
          <details className="rounded-sm border border-white/10 p-4" open data-testid="preview-trace">
            <summary className="label-caps cursor-pointer text-signal">Calculation trace</summary>
            <ol className="mt-3 space-y-1 font-mono text-[11px] text-zinc-400">{res.trace.map((t, i) => <li key={i}>{t}</li>)}</ol>
          </details>
        </div>
      )}
    </section>
  );
}
